import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarDays, Video, BookOpen, ClipboardCheck,
  TrendingUp, Menu, X, LogOut, Sparkles, Award, ShieldCheck,
  Megaphone, Medal, FolderOpen, Settings, CalendarRange, Bell, Drama, UserCheck, NotebookPen, MessageSquare, Clock8, Shirt, Inbox, Star,
  Home, Building2, HeartHandshake, GraduationCap, ChevronDown, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Avatar } from "@/components/shared";
import { cn } from "@/lib/utils";

const NAV = {
  director: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/people", label: "People", icon: Users },
    { to: "/join-requests", label: "New Inquiries", icon: Inbox, badge: "joinNew" },
    { to: "/costumes", label: "Costume Hub", icon: Shirt },
    { to: "/production?tab=characters", label: "Leads", icon: Star },
    { to: "/classes", label: "Classes", icon: CalendarDays },
    { to: "/videos", label: "Practice Videos", icon: Video },
    { to: "/curriculum", label: "Curriculum", icon: BookOpen },
    { to: "/advancement", label: "Advancement", icon: TrendingUp },
    { to: "/charms", label: "Charms", icon: Award },
    { to: "/nhsda", label: "Honor Society", icon: Medal },
    { to: "/production", label: "Production Hub", icon: Drama },
    { to: "/children-privacy", label: "Children's Privacy", icon: ShieldCheck },
    { to: "/coverage", label: "Class Coverage", icon: UserCheck },
    { to: "/attendance", label: "Attendance", icon: ClipboardCheck },
    { to: "/pickup", label: "Pickup", icon: UserCheck },
    { to: "/timesheets", label: "Timesheets", icon: Clock8 },
    { to: "/lesson-plans", label: "Lesson Plans", icon: NotebookPen },
    { to: "/messages", label: "Staff Messages", icon: MessageSquare },
    { to: "/announcements", label: "Announcements", icon: Megaphone },
    { to: "/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/documents", label: "Documents", icon: FolderOpen },
    { to: "/directors", label: "Director Management", icon: ShieldCheck },
    { to: "/settings", label: "Studio Settings", icon: Settings },
  ],
  teacher: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/classes", label: "My Classes", icon: CalendarDays },
    { to: "/assess", label: "Track Skills", icon: ClipboardCheck },
    { to: "/costumes", label: "Costume Hub", icon: Shirt },
    { to: "/production?tab=characters", label: "Leads", icon: Star },
    { to: "/curriculum", label: "Curriculum", icon: BookOpen },
    { to: "/videos", label: "Practice Videos", icon: Video },
    { to: "/advancement", label: "Advancement", icon: TrendingUp },
    { to: "/charms", label: "Charms", icon: Award },
    { to: "/nhsda", label: "Honor Society", icon: Medal },
    { to: "/production", label: "Production Hub", icon: Drama },
    { to: "/coverage", label: "Class Coverage", icon: UserCheck },
    { to: "/attendance", label: "Attendance", icon: ClipboardCheck },
    { to: "/pickup", label: "Pickup", icon: UserCheck },
    { to: "/timesheets", label: "Timesheets", icon: Clock8 },
    { to: "/lesson-plans", label: "Lesson Plans", icon: NotebookPen },
    { to: "/messages", label: "Staff Messages", icon: MessageSquare },
    { to: "/announcements", label: "Announcements", icon: Megaphone },
    { to: "/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/documents", label: "Documents", icon: FolderOpen },
  ],
  parent: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/progress", label: "My Dancers", icon: TrendingUp },
    { to: "/recital", label: "Recital Hub", icon: Drama },
    { to: "/costumes", label: "My Costumes", icon: Shirt },
    { to: "/consent", label: "Parental Consent", icon: ShieldCheck },
    { to: "/pickup", label: "Pickup", icon: UserCheck },
    { to: "/classes", label: "Classes", icon: CalendarDays },
    { to: "/videos", label: "Practice Videos", icon: Video },
    { to: "/charms", label: "Charms", icon: Award },
    { to: "/nhsda", label: "Honor Society", icon: Medal },
    { to: "/production", label: "Full Show", icon: Drama },
    { to: "/announcements", label: "Announcements", icon: Megaphone },
    { to: "/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/documents", label: "Documents", icon: FolderOpen },
  ],
  dancer: [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard },
    { to: "/progress", label: "My Progress", icon: Sparkles },
    { to: "/recital", label: "Recital Hub", icon: Drama },
    { to: "/costumes", label: "My Costumes", icon: Shirt },
    { to: "/classes", label: "My Classes", icon: CalendarDays },
    { to: "/videos", label: "Practice Videos", icon: Video },
    { to: "/charms", label: "My Charms", icon: Award },
    { to: "/nhsda", label: "Honor Society", icon: Medal },
    { to: "/production", label: "Full Show", icon: Drama },
    { to: "/announcements", label: "Announcements", icon: Megaphone },
    { to: "/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/documents", label: "Documents", icon: FolderOpen },
  ],
};

const GROUPS = [
  { key: "home", label: "Home", icon: Home, items: ["/dashboard", "/calendar"] },
  { key: "studio", label: "Studio Management", icon: Building2, items: ["/classes", "/attendance", "/pickup", "/coverage", "/timesheets", "/settings"] },
  { key: "families", label: "Dancers & Families", icon: HeartHandshake, items: ["/people", "/join-requests", "/production?tab=characters", "/advancement", "/charms", "/nhsda", "/children-privacy"] },
  { key: "teaching", label: "Teaching", icon: GraduationCap, items: ["/curriculum", "/lesson-plans", "/videos", "/assess"] },
  { key: "productions", label: "Productions", icon: Drama, items: ["/production", "/costumes", "/documents"] },
  { key: "comms", label: "Communication & Staff", icon: MessageSquare, items: ["/announcements", "/messages", "/directors"] },
];
const GROUPED = new Set(["director", "teacher"]);
const GROUP_KEY = "sv_nav_group";
const COLLAPSE_KEY = "sv_nav_collapsed";

const ROLE_LABEL = { director: "Director", teacher: "Teacher", parent: "Parent", dancer: "Dancer" };

function NotificationBell({ align = "right", idSuffix = "" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const clickItem = async (n) => { setOpen(false); if (!n.read) await api.post(`/notifications/${n.id}/read`); navigate(n.link || "/announcements"); load(); };
  const side = align === "left" ? "left-0" : "right-0";
  return (
    <div className="relative z-50">
      <button data-testid={`notification-bell${idSuffix ? "-" + idSuffix : ""}`} onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative p-2 rounded-full hover:bg-teal-light transition-colors">
        <Bell className="w-5 h-5 text-gray-600" />
        {data.unread > 0 && <span data-testid={`notification-count${idSuffix ? "-" + idSuffix : ""}`} className="absolute -top-0.5 -right-0.5 bg-teal text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{data.unread > 9 ? "9+" : data.unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notification-panel" className={`absolute ${side} mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-gray-200 shadow-xl z-50 overflow-hidden`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="font-head font-bold">Notifications</p>
              {data.unread > 0 && <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-teal-dark font-semibold hover:underline">Mark all read</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {data.items.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">You're all caught up! 🎉</p> :
                data.items.map((n) => (
                  <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => clickItem(n)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-background transition-colors ${n.read ? "" : "bg-teal-light/40"}`}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="w-2 h-2 rounded-full bg-teal mt-1.5 shrink-0" />}
                      <div className={n.read ? "pl-4" : ""}>
                        <p className="font-semibold text-sm">{n.title}</p>
                        {n.body && <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [joinNew, setJoinNew] = useState(0);
  const items = NAV[user?.role] || [];

  useEffect(() => {
    if (user?.role !== "director") return;
    const load = () => api.get("/join-requests").then((r) => setJoinNew(r.data.new_count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user?.role]);

  const badges = { joinNew };
  const grouped = GROUPED.has(user?.role);
  const onLeads = location.pathname === "/production" && new URLSearchParams(location.search).get("tab") === "characters";
  const isOn = (to) => to.includes("?tab=") ? onLeads : (location.pathname === to || location.pathname.startsWith(`${to}/`)) && !(to === "/production" && onLeads);
  const groups = GROUPS.map((g) => ({ ...g, list: g.items.map((to) => items.find((i) => i.to === to)).filter(Boolean) })).filter((g) => g.list.length);
  const current = groups.find((g) => g.list.some((i) => isOn(i.to)))?.key;
  const [openGroup, setOpenGroup] = useState(() => localStorage.getItem(GROUP_KEY) ?? "home");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  useEffect(() => { if (current) { setOpenGroup(current); localStorage.setItem(GROUP_KEY, current); } }, [current]);
  const toggleGroup = (k) => { const v = openGroup === k ? "" : k; setOpenGroup(v); localStorage.setItem(GROUP_KEY, v); };
  const toggleCollapsed = () => { const v = !collapsed; setCollapsed(v); localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0"); };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItem = (it, mini, nested) => (
    <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} title={mini ? it.label : undefined} aria-label={it.label}
      data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={cn("flex items-center gap-3 rounded-xl text-sm font-semibold transition-colors duration-200 relative", mini ? "justify-center p-2.5" : nested ? "pl-9 pr-3 py-2" : "px-3.5 py-2.5",
        isOn(it.to) ? "bg-teal text-white shadow-sm" : "text-gray-600 hover:bg-teal-light hover:text-teal-dark")}>
      <it.icon className="w-[18px] h-[18px] shrink-0" />
      {!mini && it.label}
      {it.badge && badges[it.badge] > 0 && (
        <span data-testid="nav-join-badge" className={cn("bg-teal text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ring-2 ring-white", mini ? "absolute -top-1 -right-1" : "ml-auto")}>
          {badges[it.badge] > 9 ? "9+" : badges[it.badge]}
        </span>
      )}
    </NavLink>
  );
  const groupedNav = (mini) => groups.map((g, gi) => {
    if (mini) return <div key={g.key} data-testid={`nav-group-${g.key}`} className={cn("space-y-1", gi && "pt-2 mt-2 border-t border-gray-100")}>{g.list.map((it) => navItem(it, true))}</div>;
    const isOpen = openGroup === g.key;
    const hasActive = g.key === current;
    return (
      <div key={g.key}>
        <button type="button" data-testid={`nav-group-${g.key}`} aria-expanded={isOpen} onClick={() => toggleGroup(g.key)}
          className={cn("w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13.5px] font-bold transition-colors duration-200", hasActive ? "text-teal-dark" : "text-gray-800", "hover:bg-teal-light")}>
          <g.icon className={cn("w-[18px] h-[18px]", hasActive ? "text-teal" : "text-gray-500")} />
          <span className="flex-1 text-left whitespace-nowrap" title={g.label}>{g.label}</span>
          {!isOpen && g.list.some((i) => i.badge && badges[i.badge] > 0) && <span className="w-2 h-2 rounded-full bg-teal" />}
          <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform duration-200", isOpen && "rotate-180")} />
        </button>
        {isOpen && <div className="mt-0.5 mb-1 space-y-0.5" data-testid={`nav-group-items-${g.key}`}>{g.list.map((it) => navItem(it, false, true))}</div>}
      </div>
    );
  });

  const sidebar = (mini, mobile) => (
    <div className="flex flex-col h-full">
      <div className={cn("flex items-center gap-2.5 h-16 border-b border-gray-100", mini ? "justify-center px-2" : "justify-between px-6")}>
        <div className="flex items-center gap-2.5 min-w-0">
          <img src="/logo.png" alt="Studio V" className="w-9 h-9 rounded-xl object-contain" />
          {!mini && <span className="font-head font-extrabold text-xl tracking-tight truncate">Studio V</span>}
        </div>
        {!mini && !mobile && <NotificationBell align="left" idSuffix="desktop" />}
      </div>
      {mini && <div className="flex justify-center pt-2"><NotificationBell align="left" idSuffix="desktop" /></div>}
      <nav className={cn("flex-1 py-4 space-y-1 overflow-y-auto", mini ? "px-2" : "px-3")} data-testid="sidebar-nav">
        {grouped ? groupedNav(mini) : items.map((it) => navItem(it, mini))}
      </nav>
      <div className={cn("border-t border-gray-100", mini ? "p-2" : "p-3")}>
        {!mini && <div className="flex items-center gap-3 px-2 py-2">
          <Avatar first={user?.name} last="" photo={user?.picture} size={38} />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm truncate">{user?.name}</p>
            <p className="text-xs text-teal-dark font-medium">{ROLE_LABEL[user?.role]}</p>
          </div>
        </div>}
        <button data-testid="logout-button" onClick={doLogout} title={mini ? "Log out" : undefined}
          className={cn("mt-1 w-full flex items-center gap-2 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors", mini ? "justify-center" : "px-3")}>
          <LogOut className="w-4 h-4" /> {!mini && "Log out"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className={cn("hidden lg:flex fixed inset-y-0 left-0 bg-white border-r border-gray-200 flex-col z-30 transition-[width] duration-200", collapsed ? "w-20" : "w-64")}>
        {sidebar(collapsed)}
        <button type="button" data-testid="sidebar-collapse-toggle" onClick={toggleCollapsed} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar to icons"} title={collapsed ? "Expand sidebar" : "Collapse to icons"}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-teal-dark">
          {collapsed ? <PanelLeftOpen className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Studio V" className="w-8 h-8 rounded-lg object-contain" />
          <span className="font-head font-extrabold text-lg">Studio V</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell align="right" idSuffix="mobile" />
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="p-2">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 inset-y-0 w-72 max-w-[85vw] bg-white shadow-xl" data-testid="mobile-drawer">
            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="absolute right-3 top-4 p-1 z-10 bg-white">
              <X className="w-5 h-5" />
            </button>
            {sidebar(false, true)}
          </div>
        </div>
      )}

      <main className={cn("transition-[padding] duration-200", collapsed ? "lg:pl-20" : "lg:pl-64")}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
